import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router'
import { Loader2 } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { useAccountExport } from '@/hooks/useAccountExport'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { useDateFormatter } from '@/hooks/useDateFormatter'

export function AccountExportPage() {
  const { t } = useTranslation('account')
  const { token } = useParams<{ token: string }>()
  const formatDate = useDateFormatter()

  const { data, isLoading, error } = useAccountExport(token ?? '')
  useDocumentTitle(data ? t('export.title') : '')

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    const status = (error as { response?: { status: number } }).response?.status
    if (status === 403) return <p>{t('export.not_owner')}</p>
    if (status === 404) return <p>{t('export.link_invalid')}</p>
    if (status === 410) return <p>{t('export.expired')}</p>
    return <p>{t('export.load_error')}</p>
  }

  if (!data) return null

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('export.title') }]} />
      <PageHeader title={t('export.title')} />
      <p className="text-muted-foreground">{t('export.lead')}</p>

      <ul className="space-y-2">
        {data.files.map((file) => (
          <li key={file.name}>
            <a href={file.url} download target="_blank" rel="noreferrer">
              {file.name}
            </a>
          </li>
        ))}
      </ul>

      <p className="text-muted-foreground text-sm">{t('export.links_expire')}</p>
      <p className="text-muted-foreground text-sm">
        {t('export.files_deleted_on', { date: formatDate(data.expires_at) })}
      </p>
    </div>
  )
}
